import { useState } from 'react';

import {
  AuthByOtp,
  AuthorizationError,
  lastOtpRequestAtom,
} from '~features/otp';

import { loginAtom, useInitiateMutation } from '~entities/auth';

import { HeaderWithClose } from '~shared/header-with-close/header-with-close';
import { nonNullableValue } from '~shared/types/non-nullable-value';
import { Screen } from '~shared/ui/screen';
import { ScrollIntoView } from '~shared/ui/scroll-into-view';
import { Step, Stepper } from '~shared/ui/stepper';

import { useSetAtom } from 'jotai';

import { OfferDetailsForm } from '../offer-phone-form/offer-phone-form';

const stepsNames = {
  clientPhone: 'clientPhone',
  smsConfirmation: 'smsConfirmation',
};

type Props = {
  onLogin: (phone: string) => void;
  isLoading: boolean;
  isSearchOfferError: boolean;
};

export const OfferAuth: React.FC<Props> = ({
  onLogin,
  isLoading,
  isSearchOfferError,
}) => {
  const setLastOtpRequest = useSetAtom(lastOtpRequestAtom);
  const login = useSetAtom(loginAtom);

  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [stepName, setStepName] = useState(stepsNames.clientPhone);
  const [isAuthError, setIsAuthError] = useState<boolean>(false);

  const { mutate: initiate, isPending: isInitiateInProgress } =
    useInitiateMutation({
      onSuccess(_d, params) {
        setIsAuthError(false);
        setLastOtpRequest({
          phone: params.phoneNumber,
          time: new Date().getTime(),
        });
        setPhoneNumber(params.phoneNumber);
        setStepName('smsConfirmation');
      },
      onError() {
        setIsAuthError(true);
      },
    });

  const handleLoginSuccess = (phone: string) => {
    login(phone);
    onLogin?.(phone);
    setStepName(stepsNames.clientPhone);
  };

  const handleClientInfoSubmit = async (phone: string) => {
    setIsAuthError(false);
    setPhoneNumber(phone);
    initiate({ phoneNumber: phone });
  };

  if (isAuthError) {
    return (
      <Screen
        header={
          <HeaderWithClose
            onClose={() => {
              setIsAuthError(false);
              setStepName(stepsNames.clientPhone);
            }}
            text="Enter the code"
          />
        }
        className="fixed inset-0 z-10 bg-white [&>*:first-child]:top-auto"
      >
        <AuthorizationError />
      </Screen>
    );
  }

  return (
    <Stepper current={stepName}>
      <Step name={stepsNames.clientPhone}>
        <OfferDetailsForm
          isSearchOfferError={isSearchOfferError}
          isLoading={isInitiateInProgress || isLoading}
          onSubmit={handleClientInfoSubmit}
        />
      </Step>
      <Step name={stepsNames.smsConfirmation}>
        <Screen
          header={
            <HeaderWithClose
              onClose={() => {
                setIsAuthError(false);
                setStepName(stepsNames.clientPhone);
              }}
              text="Enter the code"
            />
          }
          className="fixed inset-0 z-10 bg-white [&>*:first-child]:top-auto"
        >
          <ScrollIntoView>
            <AuthByOtp
              phoneNumber={nonNullableValue(phoneNumber)}
              onSuccess={() =>
                handleLoginSuccess(nonNullableValue(phoneNumber))
              }
              onAuthError={() => setIsAuthError(true)}
            />
          </ScrollIntoView>
        </Screen>
      </Step>
    </Stepper>
  );
};
